import { Injectable, inject } from '@angular/core';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { Observable, of, forkJoin } from 'rxjs';
import { switchMap, take, map } from 'rxjs/operators';
import { IAnalyticsService } from './analytics-service.interface';
import { AuthService } from './auth.service';
import { 
  OrganizationSummaryInsight, 
  ProviderVolumeMetric, 
  FacilityVolumeMetric,
  ParameterizedReport,
  DashboardParameters
} from '../models/analytics.model';

@Injectable({
  providedIn: 'root'
})
export class FirestoreAnalyticsService implements IAnalyticsService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);

  private getOrgPath(): Observable<string> {
    return this.authService.getLoggedInUser().pipe(
      take(1),
      map(user => {
        if (!user || !user.orgCode) {
          throw new Error('[FirestoreAnalyticsService] No authenticated user or missing orgCode.');
        }
        return `provider-orgs/provider-org-${user.orgCode}`;
      })
    );
  }

  getDailySummary(date: string): Observable<OrganizationSummaryInsight> {
    return this.getOrgPath().pipe(
      switchMap(orgPath => {
        const docRef = doc(this.firestore, `${orgPath}/insights/data/daily-summaries/${date}`);
        return docData<any>(docRef, { idField: 'id' }).pipe(take(1)) as Observable<OrganizationSummaryInsight>;
      })
    );
  }

  getDailySummaryRange(dates: string[]): Observable<OrganizationSummaryInsight[]> {
    if (!dates || dates.length === 0) return of([]);
    const requests = dates.map(date => this.getDailySummary(date));
    return forkJoin(requests);
  }

  getMonthlySummary(date: string): Observable<OrganizationSummaryInsight> {
    const formattedDate = this.formatMonthlyDate(date);
    return this.getOrgPath().pipe(
      switchMap(orgPath => {
        const docRef = doc(this.firestore, `${orgPath}/insights/data/monthly-summaries/${formattedDate}`);
        return docData<any>(docRef, { idField: 'id' }).pipe(take(1)) as Observable<OrganizationSummaryInsight>;
      })
    );
  }

  getMonthlySummaryRange(months: string[]): Observable<OrganizationSummaryInsight[]> {
    if (!months || months.length === 0) return of([]);
    const requests = months.map(month => this.getMonthlySummary(month));
    return forkJoin(requests);
  }

  getDailyProviderVolume(date: string, providerId: string): Observable<ProviderVolumeMetric> {
    return this.getOrgPath().pipe(
      switchMap(orgPath => {
        const docRef = doc(this.firestore, `${orgPath}/insights/data/daily-summaries/${date}/provider-volume/${providerId}`);
        return docData<any>(docRef, { idField: 'id' }).pipe(take(1)) as Observable<ProviderVolumeMetric>;
      })
    );
  }

  getMonthlyProviderVolume(date: string, providerId: string): Observable<ProviderVolumeMetric> {
    const formattedDate = this.formatMonthlyDate(date);
    return this.getOrgPath().pipe(
      switchMap(orgPath => {
        const docRef = doc(this.firestore, `${orgPath}/insights/data/monthly-summaries/${formattedDate}/provider-volume/${providerId}`);
        return docData<any>(docRef, { idField: 'id' }).pipe(take(1)) as Observable<ProviderVolumeMetric>;
      })
    );
  }

  getDailyFacilityVolume(date: string, facilityId: string): Observable<FacilityVolumeMetric> {
    return this.getOrgPath().pipe(
      switchMap(orgPath => {
        const docRef = doc(this.firestore, `${orgPath}/insights/data/daily-summaries/${date}/facility-volume/${facilityId}`);
        return docData<any>(docRef, { idField: 'id' }).pipe(take(1)) as Observable<FacilityVolumeMetric>;
      })
    );
  }

  getMonthlyFacilityVolume(date: string, facilityId: string): Observable<FacilityVolumeMetric> {
    const formattedDate = this.formatMonthlyDate(date);
    return this.getOrgPath().pipe(
      switchMap(orgPath => {
        const docRef = doc(this.firestore, `${orgPath}/insights/data/monthly-summaries/${formattedDate}/facility-volume/${facilityId}`);
        return docData<any>(docRef, { idField: 'id' }).pipe(take(1)) as Observable<FacilityVolumeMetric>;
      })
    );
  }

  getParameterizedReport(date: string, isMonthly?: boolean): Observable<ParameterizedReport> {
    const docPath = isMonthly 
      ? `insights/data/monthly-summaries/${this.formatMonthlyDate(date)}/parameterized-report/data`
      : `insights/data/daily-summaries/${date}/parameterized-report/data`;
    return this.getOrgPath().pipe(
      switchMap(orgPath => {
        const docRef = doc(this.firestore, `${orgPath}/${docPath}`);
        return docData<any>(docRef).pipe(take(1)) as Observable<ParameterizedReport>;
      })
    );
  }

  getParameterizedReportRange(dates: string[], isMonthly?: boolean): Observable<ParameterizedReport[]> {
    if (!dates || dates.length === 0) return of([]);
    const requests = dates.map(date => this.getParameterizedReport(date, isMonthly));
    return forkJoin(requests);
  }

  private formatMonthlyDate(date: string): string {
    const parts = date.split('-');
    if (parts.length >= 2) {
      return `${parts[0]}-${parts[1]}-01`;
    }
    return date;
  }

  getDashboardParameters(): Observable<DashboardParameters> {
    return this.getOrgPath().pipe(
      switchMap(orgPath => {
        const docRef = doc(this.firestore, `${orgPath}/insights/data/metadata/parameters`);
        return docData<any>(docRef).pipe(take(1)) as Observable<DashboardParameters>;
      })
    );
  }
}
